import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
} from "@tanstack/react-table";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { Fragment, useEffect, useMemo, useState } from "react";
import { setCollectionsList } from "../../reducer/collection/Collection.action";
import PostRequest from "../../services/PostRequest";
import { showToaster } from "../../common/common";
import { useNavigate } from "react-router-dom";
import { CollectionLoader } from "../../loader/Loader";
import { McaModal, McaSegmentedControl, McaPagination } from "../../components/mca";

const SUPPLIER_OPTIONS = [
  { id: "all", label: "All" },
  { id: "amazon", label: "Amazon" },
  { id: "officeworks", label: "Officeworks" },
  { id: "repco", label: "Repco" },
];

const STATUS_CONFIG = {
  Completed: { cls: "soft-success", label: "Completed" },
  Processing: { cls: "soft-info", label: "Processing" },
  Pending: { cls: "soft-warning", label: "Pending" },
  Failed: { cls: "soft-danger", label: "Failed" },
};

const SUPPLIER_ICONS = {
  amazon: (
    <svg
      viewBox="0 0 24 24"
      width="12"
      height="12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 16c4 3 14 3 18 0" />
      <path d="M19 14c1 3-1 5-1 5" />
    </svg>
  ),
  officeworks: (
    <svg
      viewBox="0 0 24 24"
      width="12"
      height="12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 21V8l8-5 8 5v13" />
      <path d="M9 21v-6h6v6" />
    </svg>
  ),
  repco: (
    <svg
      viewBox="0 0 24 24"
      width="12"
      height="12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 13l1.5-4.5A2 2 0 0 1 8.4 7h7.2a2 2 0 0 1 1.9 1.5L19 13v5h-2v-2H7v2H5z" />
      <circle cx="8" cy="16" r="1" />
      <circle cx="16" cy="16" r="1" />
    </svg>
  ),
};

const numberFormatter = (num) => {
  if (num === null || num === undefined) return "0";
  return num.toLocaleString();
};

const PAGE_SIZE = 50;

/**
 * Supplier filter control — rendered at page level
 */
export function SupplierFilter({ value = "all", onChange }) {
  return (
    <McaSegmentedControl
      options={SUPPLIER_OPTIONS}
      value={value}
      onChange={onChange}
      data-testid="supplier-filter"
    />
  );
}

/**
 * CollectionsTable — Main table component with all interactions
 */
export const CollectionsTable = ({ supplierFilter = "all" }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const collectionList = useSelector(
    (state) => state.collectionReducer.collectionList || []
  );

  // State
  const [loader, setLoader] = useState(false);
  const [sorting, setSorting] = useState([]);
  const [selectedRows, setSelectedRows] = useState({});
  const [runNowModal, setRunNowModal] = useState({ open: false, collection: null, credits: 0 });
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);
  const [deleteRowModal, setDeleteRowModal] = useState({ open: false, collection: null });
  const [page, setPage] = useState(1);

  // Filter collections by supplier (memoised so unrelated re-renders — pagination,
  // opening a row menu — don't rebuild the array and force TanStack to reprocess).
  const filteredCollections = useMemo(
    () =>
      supplierFilter === "all"
        ? collectionList
        : collectionList.filter(
            (c) => (c.supplier || "").toLowerCase() === supplierFilter
          ),
    [collectionList, supplierFilter]
  );

  // Pagination — 50 collections per page (matches the design).
  const totalPages = Math.max(1, Math.ceil(filteredCollections.length / PAGE_SIZE));
  // Reset to page 1 when the supplier filter changes.
  useEffect(() => {
    setPage(1);
  }, [supplierFilter]);
  // Clamp the current page if the list shrinks (e.g. after deletes).
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  // Load collections on mount
  useEffect(() => {
    const loadCollections = async () => {
      try {
        setLoader(true);
        await dispatch(setCollectionsList());
      } catch (error) {
        console.error("Error loading collections:", error);
      } finally {
        setLoader(false);
      }
    };
    loadCollections();
  }, [dispatch]);

  // Handlers
  const handleDeleteCollection = async (id) => {
    try {
      const data = { schedule_id: id };
      await PostRequest("/schedules/delete_schedule/", data);
      showToaster("Collection deleted successfully.");
      dispatch(setCollectionsList());
      setDeleteRowModal({ open: false, collection: null });
    } catch (error) {
      showToaster(error.message, "error");
    }
  };

  const handleOpenRecords = (id) => {
    navigate(`/collections/${id}/records`);
  };

  const handleDownloadOutput = (id) => {
    navigate(`/collections/${id}/download`);
  };

  const handleEditCollection = (collection) => {
    // /collections/:id opens the edit drawer (Collections.js reads the param and
    // passes activeModal=id to CollectionForm).
    navigate(`/collections/${collection.id}`);
  };

  // Re-dispatch a category scrape (retry Failed / refresh Completed). Category
  // scrapes have no ASINs, so the ASIN "Run now" doesn't apply.
  const handleRerunCategory = async (collection) => {
    try {
      await PostRequest(
        "/schedules/amazon/category/rerun/",
        { collection_id: collection.id },
        { showLoader: "rerun-" + collection.id }
      );
      showToaster("Category scrape re-queued.");
      dispatch(setCollectionsList());
    } catch (error) {
      showToaster(error.message, "error");
    }
  };

  const handleRunImmediate = async (collectionId) => {
    try {
      const payload = { collection_id: collectionId };
      const response = await PostRequest("/schedules/run/result/", payload);
      // Show success toast
      showToaster("Run started — credits reserved.");
      // Navigate to download results
      navigate(`/collections/${collectionId}/download`);
      setRunNowModal({ open: false, collection: null, credits: 0 });
    } catch (error) {
      // Surface the error (e.g., not enough credits)
      showToaster(error.message, "error");
      setRunNowModal({ open: false, collection: null, credits: 0 });
    }
  };

  const handleBulkDelete = async () => {
    try {
      const selectedIds = Object.keys(selectedRows).filter(
        (id) => selectedRows[id]
      );
      if (selectedIds.length === 0) return;

      await PostRequest("/schedules/delete_multiple/", {
        schedule_ids: selectedIds,
      });
      showToaster("Selected collections deleted successfully.");
      dispatch(setCollectionsList());
      setSelectedRows({});
      setBulkDeleteModal(false);
    } catch (error) {
      showToaster(error.message, "error");
    }
  };

  // Export the selected collections to a CSV (client-side, no backend needed).
  const handleExportSelected = () => {
    const selectedIds = Object.keys(selectedRows).filter((id) => selectedRows[id]);
    const rows = collectionList.filter((c) => selectedIds.includes(String(c.id)));
    if (rows.length === 0) return;
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["Name", "Type", "Records", "Status", "Schedule", "Updated"];
    const lines = [header.join(",")].concat(
      rows.map((c) =>
        [
          esc(c.name),
          esc(
            c.supplier
              ? `${c.supplier}${
                  c.collection_type === "category" ||
                  c.request_type === "amazon_category_scrape"
                    ? " (Category scrape)"
                    : c.supplier === "Amazon"
                    ? " (Product scrape)"
                    : ""
                }`
              : c.request_type
          ),
          esc(
            (c.collection_type === "category"
              ? c.display_count ?? c.record_count
              : c.asin_count) ?? 0
          ),
          esc(c.schedule_status ?? ""),
          esc(c.schedule ?? "Manual"),
          esc(c.updated_at ?? ""),
        ].join(",")
      )
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `collections-export-${rows.length}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    showToaster(`Exported ${rows.length} collection${rows.length === 1 ? "" : "s"} to CSV.`);
  };

  // Selection helpers
  const toggleRowSelection = (id) => {
    setSelectedRows((prev) => {
      const newSelection = { ...prev, [id]: !prev[id] };
      return newSelection;
    });
  };

  const toggleSelectAll = () => {
    const allSelected =
      filteredCollections.length > 0 &&
      Object.keys(selectedRows).length === filteredCollections.length &&
      Object.values(selectedRows).every(Boolean);

    const newSelectedRows = {};
    if (!allSelected) {
      filteredCollections.forEach((item) => {
        newSelectedRows[item.id] = true;
      });
    }
    setSelectedRows(newSelectedRows);
  };

  const isAllSelected =
    filteredCollections.length > 0 &&
    Object.keys(selectedRows).length === filteredCollections.length &&
    Object.values(selectedRows).every(Boolean);

  const selectedCount = Object.keys(selectedRows).filter(
    (id) => selectedRows[id]
  ).length;

  // Table columns
  const columns = [
    {
      id: "select",
      header: () => (
        <label className="mca-check">
          <input
            type="checkbox"
            onChange={toggleSelectAll}
            checked={isAllSelected}
            aria-label="Select all"
          />
          <span className="mca-check__box">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </span>
        </label>
      ),
      cell: ({ row }) => (
        <label className="mca-check">
          <input
            type="checkbox"
            checked={!!selectedRows[row.original.id]}
            onChange={() => toggleRowSelection(row.original.id)}
            aria-label={`Select ${row.original.name}`}
          />
          <span className="mca-check__box">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </span>
        </label>
      ),
      size: 46,
    },
    {
      accessorKey: "name",
      header: "Name",
      enableSorting: true,
      cell: ({ row }) => (
        <div className="col-name">
          <span className="nm">{row.original.name}</span>
          <span className="sub">{row.original.description || ""}</span>
        </div>
      ),
    },
    {
      accessorKey: "request_type",
      header: "Type",
      cell: ({ row }) => {
        const r = row.original;
        // Prefer the server-computed supplier; fall back for a stale cache.
        const supplier =
          r.supplier ||
          (r.request_type === "amazon"
            ? "Amazon"
            : r.request_type === "officeworks"
            ? "Officeworks"
            : r.request_type === "repco"
            ? "Repco"
            : "Other");
        const isCategory =
          r.collection_type === "category" ||
          r.request_type === "amazon_category_scrape";
        // Secondary "scrape kind" badge: category scrapes vs individual-product
        // (ASIN) Amazon scrapes. Officeworks / other suppliers get no kind badge.
        const scrapeKind = isCategory
          ? "Category scrape"
          : supplier === "Amazon"
          ? "Product scrape"
          : null;
        const iconKey =
          supplier === "Amazon"
            ? "amazon"
            : supplier === "Officeworks"
            ? "officeworks"
            : supplier === "Repco"
            ? "repco"
            : null;
        const badge =
          supplier === "Amazon"
            ? "mca-badge--soft-primary"
            : "mca-badge--soft-muted";
        return (
          <span style={{ display: "inline-flex", gap: "6px", alignItems: "center" }}>
            <span className={`mca-badge ${badge}`}>
              {iconKey ? SUPPLIER_ICONS[iconKey] : null}
              {supplier}
            </span>
            {scrapeKind && (
              <span
                className={`mca-badge ${
                  isCategory ? "mca-badge--soft-info" : "mca-badge--soft-muted"
                }`}
              >
                {scrapeKind}
              </span>
            )}
          </span>
        );
      },
    },
    {
      id: "records",
      header: "Records",
      enableSorting: true,
      // accessorFn so the sort key matches the displayed number (category rows
      // would otherwise sort by their always-0 asin_count).
      accessorFn: (r) =>
        r.collection_type === "category" ||
        r.request_type === "amazon_category_scrape"
          ? r.display_count ?? r.record_count ?? 0
          : r.asin_count ?? 0,
      cell: ({ row }) => {
        const r = row.original;
        const isCategory =
          r.collection_type === "category" ||
          r.request_type === "amazon_category_scrape";
        const count = isCategory
          ? r.display_count ?? r.record_count ?? 0
          : r.asin_count || 0;
        const unit =
          r.display_count_label || (isCategory ? "products" : "records");
        return (
          <>
            <span className="cell-strong">{numberFormatter(count)}</span>{" "}
            <span className="cell-muted" style={{ fontSize: "12px" }}>
              {unit}
            </span>
          </>
        );
      },
    },
    {
      accessorKey: "schedule_status",
      header: "Status",
      cell: ({ row }) => {
        const status = row.original.schedule_status;
        // Unknown/empty statuses must NOT default to "Completed" — show the real
        // value with a neutral badge (a Pending job is not done).
        const config =
          STATUS_CONFIG[status] || { cls: "soft-muted", label: status || "Unknown" };
        return (
          <span className={`mca-badge mca-badge--${config.cls}`}>
            <span className="dot"></span>
            {config.label}
          </span>
        );
      },
    },
    {
      accessorKey: "schedule",
      header: "Schedule",
      cell: ({ row }) => {
        const c = row.original;
        const isCategory =
          c.collection_type === "category" ||
          c.request_type === "amazon_category_scrape";
        // Category scrapes are one-shot auto-dispatched runs, not user-triggered
        // "Manual" jobs — label them accordingly.
        return (
          <span className="cell-muted hide-md">
            {isCategory ? "One-off" : c.schedule || "Manual"}
          </span>
        );
      },
    },
    {
      accessorKey: "updated_at",
      header: "Updated",
      enableSorting: true,
      cell: ({ row }) => (
        <span className="cell-muted hide-md" style={{ fontSize: "13px" }}>
          {row.original.updated_at
            ? moment(row.original.updated_at).fromNow()
            : "Never"}
        </span>
      ),
    },
    {
      id: "actions",
      header: () => <span style={{ textAlign: "right" }}>Actions</span>,
      cell: ({ row }) => {
        const collection = row.original;
        const isCategory =
          collection.collection_type === "category" ||
          collection.request_type === "amazon_category_scrape";
        const canRun =
          collection.asin_count > 0 && collection.schedule_status !== "Processing";
        const canRerun =
          isCategory &&
          (collection.schedule_status === "Completed" ||
            collection.schedule_status === "Failed");

        return (
          <div className="mca-row-actions">
            {/* Category → Re-run (re-dispatch); ASIN/SKU → Run now */}
            {isCategory ? (
              <button
                className="mca-btn mca-btn--ghost mca-btn--icon mca-btn--sm"
                disabled={!canRerun}
                title={canRerun ? "Re-run scrape" : "Scrape is running or queued"}
                aria-label="Re-run scrape"
                type="button"
                onClick={canRerun ? () => handleRerunCategory(collection) : undefined}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                  <path d="M3 3v5h5" />
                </svg>
              </button>
            ) : canRun ? (
              <button
                className="mca-btn mca-btn--ghost mca-btn--icon mca-btn--sm runnow"
                title="Run now"
                aria-label="Run now"
                onClick={() =>
                  setRunNowModal({ open: true, collection, credits: collection.asin_count || 0 })
                }
                type="button"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m6 4 14 8-14 8V4Z" />
                </svg>
              </button>
            ) : (
              <button
                className="mca-btn mca-btn--ghost mca-btn--icon mca-btn--sm"
                disabled
                title="Add records to run"
                aria-label="Run now"
                type="button"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m6 4 14 8-14 8V4Z" />
                </svg>
              </button>
            )}

            {/* Edit */}
            <button
              className="mca-btn mca-btn--ghost mca-btn--icon mca-btn--sm"
              title="Edit collection"
              aria-label="Edit collection"
              type="button"
              onClick={() => handleEditCollection(collection)}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 20h4l10-10a2 2 0 0 0-3-3L5 17v3Z" />
                <path d="m13.5 6.5 3 3" />
              </svg>
            </button>

            {/* View records */}
            <button
              className="mca-btn mca-btn--ghost mca-btn--icon mca-btn--sm"
              title="View records"
              aria-label="View records"
              type="button"
              onClick={() => handleOpenRecords(collection.id)}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                <circle cx="12" cy="12" r="2.6" />
              </svg>
            </button>

            {/* Download results */}
            <button
              className="mca-btn mca-btn--ghost mca-btn--icon mca-btn--sm"
              title="Download results"
              aria-label="Download results"
              type="button"
              onClick={() => handleDownloadOutput(collection.id)}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 3v12" />
                <path d="m8 11 4 4 4-4" />
                <path d="M4 19h16" />
              </svg>
            </button>

            {/* Delete */}
            <button
              className="mca-btn mca-btn--ghost mca-btn--icon mca-btn--sm"
              title="Delete collection"
              aria-label="Delete collection"
              type="button"
              style={{ color: "var(--mca-danger)" }}
              onClick={() => setDeleteRowModal({ open: true, collection })}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 7h16" />
                <path d="M9 7V4h6v3" />
                <path d="M6 7l1 13h10l1-13" />
              </svg>
            </button>
          </div>
        );
      },
    },
  ];

  // React Table instance
  const tableInstance = useReactTable({
    columns,
    data: filteredCollections,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
    onSortingChange: setSorting,
  });

  // Sort the full filtered set, then show only the current page's slice.
  const sortedRows = tableInstance.getRowModel().rows;
  const startIdx = (page - 1) * PAGE_SIZE;
  const pageRows = sortedRows.slice(startIdx, startIdx + PAGE_SIZE);

  return (
    <>
      {/* Bulk selection bar */}
      {selectedCount > 0 && (
        <div
          className="mca-bulkbar"
          style={{ marginBottom: "14px" }}
        >
          <strong>
            <span>{selectedCount}</span> selected
          </strong>
          <div style={{ marginLeft: "auto", display: "flex", gap: "8px" }}>
            <button
              className="mca-btn mca-btn--outline mca-btn--sm"
              type="button"
              onClick={handleExportSelected}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 3v12" />
                <path d="m8 11 4 4 4-4" />
                <path d="M4 19h16" />
              </svg>
              Export
            </button>
            <button
              className="mca-btn mca-btn--danger mca-btn--sm"
              onClick={() => setBulkDeleteModal(true)}
              type="button"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 7h16" />
                <path d="M9 7V4h6v3" />
                <path d="M6 7l1 13h10l1-13" />
              </svg>
              Delete selected
            </button>
          </div>
          <button
            className="mca-overlay-close"
            onClick={() => setSelectedRows({})}
            aria-label="Clear selection"
            type="button"
            style={{ background: "rgba(255,255,255,.15)", color: "#fff" }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Collections table */}
      <div className="mca-table-wrap">
        <table className="mca-table" id="collectionsTable">
          <thead>
            {tableInstance.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const sortableHeader =
                    header.column.columnDef.enableSorting &&
                    header.id !== "select" &&
                    header.id !== "actions";
                  return (
                    <th key={header.id}>
                      {sortableHeader ? (
                        <span
                          className="mca-th-sort"
                          onClick={header.column.getToggleSortingHandler()}
                          style={{ cursor: "pointer" }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="m8 9 4-4 4 4" />
                            <path d="m16 15-4 4-4-4" />
                          </svg>
                        </span>
                      ) : (
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody id="collectionsBody">
            {loader ? (
              <CollectionLoader />
            ) : filteredCollections.length === 0 ? (
              <tr>
                <td
                  colSpan={tableInstance.getAllColumns().length}
                  style={{ textAlign: "center", border: "none" }}
                >
                  <h6 style={{ marginTop: "32px" }}>No collections yet.</h6>
                </td>
              </tr>
            ) : (
              pageRows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Table footer with real pagination (50 per page) */}
        <div className="table-foot">
          <span id="collCount">
            {filteredCollections.length === 0
              ? "No collections"
              : `Showing ${startIdx + 1}–${Math.min(
                  startIdx + PAGE_SIZE,
                  filteredCollections.length
                )} of ${filteredCollections.length} collections`}
          </span>
          <McaPagination page={page} totalPages={totalPages} onChange={setPage} />
        </div>
      </div>

      {/* Run Now Confirmation Modal */}
      <McaModal
        open={runNowModal.open}
        onClose={() =>
          setRunNowModal({ open: false, collection: null, credits: 0 })
        }
        title="Run collection now?"
        static
        footer={
          <>
            <button
              className="mca-btn mca-btn--outline"
              onClick={() =>
                setRunNowModal({ open: false, collection: null, credits: 0 })
              }
              type="button"
            >
              Cancel
            </button>
            <button
              className="mca-btn mca-btn--primary"
              onClick={() =>
                runNowModal.collection &&
                handleRunImmediate(runNowModal.collection.id)
              }
              type="button"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m6 4 14 8-14 8V4Z" />
              </svg>
              <span className="mca-btn__label">Run now</span>
            </button>
          </>
        }
      >
        <p>
          This runs{" "}
          <strong>
            {runNowModal.collection ? runNowModal.collection.name : "collection"}
          </strong>
          {" "}immediately, outside its schedule.
        </p>
        <div className="mca-alert mca-alert--warning">
          <svg
            className="lead"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M10.3 3.8 2.4 17.5A2 2 0 0 0 4.1 20.5h15.8a2 2 0 0 0 1.7-3L13.7 3.8a2 2 0 0 0-3.4 0Z" />
            <path d="M12 9v4.5" />
            <path d="M12 17h.01" />
          </svg>
          <div className="mca-alert__body">
            This will consume <strong>{runNowModal.credits} credits</strong> from
            your balance. Results appear under Downloads when the run completes.
          </div>
        </div>
      </McaModal>

      {/* Bulk Delete Confirmation Modal */}
      <McaModal
        open={bulkDeleteModal}
        onClose={() => setBulkDeleteModal(false)}
        title="Delete selected collections?"
        static
        footer={
          <>
            <button
              className="mca-btn mca-btn--outline"
              onClick={() => setBulkDeleteModal(false)}
              type="button"
            >
              Cancel
            </button>
            <button
              className="mca-btn mca-btn--danger"
              onClick={handleBulkDelete}
              type="button"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 7h16" />
                <path d="M9 7V4h6v3" />
                <path d="M6 7l1 13h10l1-13" />
              </svg>
              <span className="mca-btn__label">Delete</span>
            </button>
          </>
        }
      >
        <p>
          This permanently removes the selected collections and their schedules.
          Records and past downloads are kept.
        </p>
      </McaModal>

      {/* Delete Single Collection Confirmation Modal */}
      <McaModal
        open={deleteRowModal.open}
        onClose={() => setDeleteRowModal({ open: false, collection: null })}
        title="Delete collection?"
        static
        footer={
          <>
            <button
              className="mca-btn mca-btn--outline"
              onClick={() => setDeleteRowModal({ open: false, collection: null })}
              type="button"
            >
              Cancel
            </button>
            <button
              className="mca-btn mca-btn--danger"
              onClick={() =>
                deleteRowModal.collection &&
                handleDeleteCollection(deleteRowModal.collection.id)
              }
              type="button"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 7h16" />
                <path d="M9 7V4h6v3" />
                <path d="M6 7l1 13h10l1-13" />
              </svg>
              <span className="mca-btn__label">Delete</span>
            </button>
          </>
        }
      >
        <p>
          This permanently removes{" "}
          <strong>
            {deleteRowModal.collection
              ? deleteRowModal.collection.name
              : "this collection"}
          </strong>
          {" "}and its schedule. Records and past downloads are kept.
        </p>
      </McaModal>
    </>
  );
};

// Export supplier filter component for page-level use
CollectionsTable.SupplierFilter = SupplierFilter;
